/**
 * components/TabularResultsView.jsx
 *
 * Reusable per-part results view for a TABULAR scan. Renders a part selector
 * plus the SAME normal-scan ResultsTable for the selected part, with per-part
 * PDF/Excel downloads, an "All (ZIP)" download, and Low-Confidence promote.
 *
 * Used by:
 *   - TabularProcessingView (live scan, behind the "View Results" button)
 *   - FileDetailModal / BatchResultsView (opening a saved tabular scan)
 *
 * `variants` is the array from GET /results (or /tabular/{id}/variants). Each
 * entry: { part_no, final_plan, ... }. The array INDEX is the variant index
 * used by the per-part download/promote endpoints.
 */

import React, { useState } from 'react';
import API from '../services/api';
import ResultsTable from './ResultsTable';

export default function TabularResultsView({ fileId, variants }) {
  const list = Array.isArray(variants) ? variants : [];
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [zipBusy, setZipBusy] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [, setTick] = useState(0);   // force re-render after an in-place promote

  if (!list.length) {
    return <div style={{ padding: 24, color: '#64748b' }}>No per-part plans found for this scan.</div>;
  }

  const safe = (s) => String(s || 'part').replace(/[^A-Za-z0-9 ._-]+/g, '_');
  const sel = list[Math.min(selectedIdx, list.length - 1)];
  const fp = sel?.final_plan || {};

  const dl = async (kind) => {
    setBusy(kind); setErr(null);
    try {
      const name = `${safe(sel.part_no)} - Inspection Plan.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
      if (kind === 'excel') await API.downloadTabularVariantExcel(fileId, selectedIdx, name);
      else await API.downloadTabularVariantPdf(fileId, selectedIdx, name);
    } catch (e) { setErr(e?.error || 'Download failed'); }
    finally { setBusy(null); }
  };
  const zip = async (type) => {
    setZipBusy(type); setErr(null);
    try { await API.downloadTabularZip(fileId, type); }
    catch (e) { setErr(e?.error || 'ZIP download failed'); }
    finally { setZipBusy(null); }
  };

  return (
    <div className="tabular-results">
      {/* Bulk actions */}
      <div className="bpv-results-cta" style={{ flexWrap: 'wrap', gap: 12 }}>
        <p className="bpv-results-cta-label">
          {list.length} part{list.length !== 1 ? 's' : ''} — each has its own inspection plan.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="bpv-view-results-btn" onClick={() => zip('pdf')} disabled={zipBusy === 'pdf'}>
            {zipBusy === 'pdf' ? 'Zipping…' : 'All PDFs (ZIP)'}
          </button>
          <button className="bpv-view-results-btn" onClick={() => zip('excel')} disabled={zipBusy === 'excel'}>
            {zipBusy === 'excel' ? 'Zipping…' : 'All Excel (ZIP)'}
          </button>
        </div>
      </div>

      {/* Part selector */}
      <div className="fps-pills" style={{ flexWrap: 'wrap', padding: '8px 0' }}>
        {list.map((v, i) => (
          <button
            key={i}
            className={`fps-pill ${i === selectedIdx ? 'processing active' : ''}`}
            onClick={() => setSelectedIdx(i)}
            title={v.part_no}
          >
            <span className="fps-pill-name">{v.part_no || `Part ${i + 1}`}</span>
          </button>
        ))}
      </div>

      {/* Selected part's plan + downloads */}
      <div className="bpv-results-cta" style={{ gap: 8 }}>
        <p className="bpv-results-cta-label">
          Inspection plan — <strong>{sel.part_no}</strong>
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="bpv-view-results-btn" onClick={() => dl('pdf')} disabled={busy === 'pdf'}>PDF</button>
          <button className="bpv-view-results-btn" onClick={() => dl('excel')} disabled={busy === 'excel'}>Excel</button>
        </div>
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 12, margin: '4px 0' }}>{err}</div>}

      <ResultsTable
        metadata={fp.metadata || {}}
        items={fp.inspection_items || []}
        notesItems={fp.notes_items || []}
        visualInspectionItems={fp.visual_inspection_items}
        safetyHazardItems={fp.safety_hazard_items || []}
        gdtTableRows={fp.gdt_table_rows || []}
        gdtSymbolsUnused={fp.gdt_symbols_unused || []}
        lowConfidenceDimensions={fp.low_confidence_dimensions || []}
        onPromoteLowConfidence={async (lcIndex, destination, overrides) => {
          const idx = selectedIdx;
          const resp = await API.promoteTabularRow(fileId, idx, lcIndex, destination, overrides);
          if (resp?.final_plan) {
            list[idx].final_plan = resp.final_plan;
            setTick((t) => t + 1);
          }
        }}
        onDemoteDimension={async (specification, location) => {
          const idx = selectedIdx;
          const resp = await API.demoteTabularRow(fileId, idx, specification, location);
          if (resp?.final_plan) {
            list[idx].final_plan = resp.final_plan;
            setTick((t) => t + 1);
          }
        }}
        onSaveItems={async (editedItems, editedMeta) => {
          const idx = selectedIdx;
          if (!list[idx]?.final_plan) return;
          const resp = await API.patchTabularVariant(fileId, idx, {
            inspection_items: editedItems,
            metadata: editedMeta,
          });
          list[idx].final_plan = resp?.final_plan
            ? resp.final_plan
            : { ...list[idx].final_plan, inspection_items: editedItems, metadata: editedMeta };
          setTick((t) => t + 1);
        }}
        onSaveNotes={async (editedNotes) => {
          const idx = selectedIdx;
          if (!list[idx]?.final_plan) return;
          await API.patchTabularVariant(fileId, idx, { notes_items: editedNotes });
          list[idx].final_plan = { ...list[idx].final_plan, notes_items: editedNotes, visual_inspection_items: null };
          setTick((t) => t + 1);
        }}
        onSaveGdt={async (editedGdt) => {
          const idx = selectedIdx;
          if (!list[idx]?.final_plan) return;
          await API.patchTabularVariant(fileId, idx, { gdt_table_rows: editedGdt });
          list[idx].final_plan = { ...list[idx].final_plan, gdt_table_rows: editedGdt };
          setTick((t) => t + 1);
        }}
        onDemoteNote={async (noteLabel, category) => {
          const idx = selectedIdx;
          const resp = await API.demoteTabularNote(fileId, idx, noteLabel, category);
          if (resp?.final_plan) {
            list[idx].final_plan = resp.final_plan;
            setTick((t) => t + 1);
          }
        }}
        onDemoteGdt={async (gdtItemNo) => {
          const idx = selectedIdx;
          const resp = await API.demoteTabularGdt(fileId, idx, gdtItemNo);
          if (resp?.final_plan) {
            list[idx].final_plan = resp.final_plan;
            setTick((t) => t + 1);
          }
        }}
        ccCount={fp.cc_count}
        itemCount={(fp.inspection_items || []).length}
        fileId={fileId}
        blockTolerances={fp.block_tolerances ?? {}}
      />
    </div>
  );
}
